import { useEffect, useState } from "react";
import { Plus, FileSignature, Send, ShieldCheck, X, Eye, Ban, FileText } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, StatusChip, EmptyState } from "@/components/Common";
import { toast } from "sonner";

export default function CreditNotes() {
  const [items, setItems] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/credit-notes").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/invoices").then((r) =>
      setInvoices(r.data.filter((i) => ["autorizada", "firmada", "enviada"].includes(i.status))),
    );
  }, []);

  const action = async (id, path) => {
    try {
      await api.post(`/credit-notes/${id}/${path}`);
      toast.success(`Operación '${path}' completada.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="credit-notes-page">
      <PageHeader
        title="Notas de Crédito"
        subtitle="Devoluciones totales o parciales sobre facturas autorizadas."
        testid="credit-notes-header"
        action={
          <button data-testid="credit-notes-create-button" onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva nota de crédito
          </button>
        }
      />

      {showForm && (
        <CreditNoteForm
          invoices={invoices}
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {items.length === 0 ? (
        <EmptyState title="Sin notas de crédito" hint="Crea una para devoluciones o ajustes." testid="credit-notes-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="credit-notes-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
              <th className="px-5 py-3">Número</th><th className="px-5 py-3">Factura origen</th>
              <th className="px-5 py-3">Cliente</th><th className="px-5 py-3">Fecha</th>
              <th className="px-5 py-3 text-right">Total</th><th className="px-5 py-3">Estado</th>
              <th className="px-5 py-3 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {items.map((n) => (
                <tr key={n.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-3 font-mono">{n.document_number}</td>
                  <td className="px-5 py-3 font-mono text-xs">{n.invoice_document_number}</td>
                  <td className="px-5 py-3">{n.customer_name}</td>
                  <td className="px-5 py-3 text-xs text-[#5C6B64]">{new Date(n.issue_date).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right font-bold">$ {Number(n.total).toFixed(2)}</td>
                  <td className="px-5 py-3"><StatusChip status={n.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button title="Ver" onClick={() => setDetail(n)} className="p-2 rounded hover:bg-[#F3F2ED]"><Eye className="w-4 h-4 text-[#5C6B64]" /></button>
                      <a title="PDF" href={`${API_BASE}/credit-notes/${n.id}/pdf`} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-[#F3F2ED] inline-flex"><FileText className="w-4 h-4 text-[#5C6B64]" /></a>
                      {n.status === "borrador" && <button title="Firmar" onClick={() => action(n.id, "sign")} className="p-2 rounded hover:bg-[#DDE6ED]"><FileSignature className="w-4 h-4 text-[#608090]" /></button>}
                      {(n.status === "firmada" || n.status === "rechazada") && <button title="Enviar" onClick={() => action(n.id, "send")} className="p-2 rounded hover:bg-[#DDE6ED]"><Send className="w-4 h-4 text-[#608090]" /></button>}
                      {["firmada","enviada","rechazada"].includes(n.status) && <button title="Autorizar" onClick={() => action(n.id, "authorize")} className="p-2 rounded hover:bg-[#E4EFE6]"><ShieldCheck className="w-4 h-4 text-[#507A59]" /></button>}
                      {n.status !== "anulada" && <button title="Anular" onClick={() => { if (window.confirm("¿Anular nota de crédito?")) action(n.id, "cancel"); }} className="p-2 rounded hover:bg-[#F3DADA]"><Ban className="w-4 h-4 text-[#A44A4A]" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && <CreditNoteDetail doc={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function CreditNoteForm({ invoices, onClose, onCreated }) {
  const [invoice_id, setInvoiceId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [returnToStock, setReturnToStock] = useState(true);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const selectedInvoice = invoices.find((i) => i.id === invoice_id);

  useEffect(() => {
    if (selectedInvoice) {
      setItems(selectedInvoice.items.map((it) => ({
        product_id: it.product_id, name: it.name, sku: it.sku,
        max_qty: it.quantity, quantity: it.quantity, unit_price: it.unit_price, include: true,
      })));
    } else setItems([]);
  }, [invoice_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!invoice_id) return toast.error("Selecciona una factura");
    if (!motivo) return toast.error("Indica el motivo");
    const selected = items.filter((i) => i.include && i.quantity > 0);
    if (selected.length === 0) return toast.error("Selecciona al menos un producto");
    setSaving(true);
    try {
      await api.post("/credit-notes", {
        invoice_id, motivo, return_to_stock: returnToStock,
        items: selected.map((i) => ({
          product_id: i.product_id, quantity: Number(i.quantity), unit_price: Number(i.unit_price),
        })),
      });
      toast.success("Nota de crédito creada");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="credit-note-form" className="card-soft p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-bold">Nueva nota de crédito</h3>
        <button onClick={onClose} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Factura a modificar *</span>
            <select data-testid="credit-note-invoice-select" required className="input-soft" value={invoice_id} onChange={(e) => setInvoiceId(e.target.value)}>
              <option value="">Seleccione</option>
              {invoices.map((i) => <option key={i.id} value={i.id}>{i.document_number} — {i.customer_name} ($ {Number(i.total).toFixed(2)})</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Motivo *</span>
            <input data-testid="credit-note-motivo" required minLength={3} className="input-soft" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Devolución por producto dañado" />
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="credit-note-return-stock" type="checkbox" checked={returnToStock} onChange={(e) => setReturnToStock(e.target.checked)} />
          Reponer stock al kardex cuando se autorice
        </label>

        {items.length > 0 && (
          <div className="border border-[#E2E0D8] rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 p-3 bg-[#F3F2ED] text-[11px] uppercase tracking-[0.16em] text-[#5C6B64]">
              <div className="col-span-1"></div>
              <div className="col-span-5">Producto</div>
              <div className="col-span-2">Facturado</div>
              <div className="col-span-2">A devolver</div>
              <div className="col-span-2">P. Unit.</div>
            </div>
            {items.map((it, idx) => (
              <div key={it.product_id || idx} className="grid grid-cols-12 gap-2 p-3 border-t border-[#E2E0D8] items-center">
                <input type="checkbox" className="col-span-1" checked={it.include} onChange={(e) => { const n = items.slice(); n[idx] = { ...it, include: e.target.checked }; setItems(n); }} />
                <div className="col-span-5"><p className="font-medium">{it.name}</p><p className="text-xs text-[#8A9A92] font-mono">{it.sku}</p></div>
                <div className="col-span-2 text-sm">{Number(it.max_qty).toFixed(2)}</div>
                <input type="number" step="0.01" max={it.max_qty} className="input-soft col-span-2" value={it.quantity} disabled={!it.include}
                  onChange={(e) => { const n = items.slice(); n[idx] = { ...it, quantity: Math.min(Number(e.target.value), it.max_qty) }; setItems(n); }} />
                <div className="col-span-2 text-sm">$ {Number(it.unit_price).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button data-testid="credit-note-save-button" disabled={saving} className="btn-primary" type="submit">{saving ? "Guardando..." : "Crear nota"}</button>
        </div>
      </form>
    </div>
  );
}

function CreditNoteDetail({ doc, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-7 border border-[#E2E0D8]">
        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#8A9A92]">Nota de Crédito</p>
            <h3 className="font-heading text-2xl font-black">{doc.document_number}</h3>
            <p className="text-sm text-[#5C6B64] mt-1">Modifica factura {doc.invoice_document_number}</p>
          </div>
          <button onClick={onClose} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
        </div>
        <div className="mb-4"><StatusChip status={doc.status} /></div>
        <p className="text-sm mb-4"><strong>Motivo:</strong> {doc.motivo}</p>
        <table className="w-full text-sm mb-4">
          <thead><tr className="text-left text-[11px] uppercase tracking-[0.16em] text-[#8A9A92]"><th className="py-2">Producto</th><th>Cant.</th><th>Precio</th><th>Subtotal</th></tr></thead>
          <tbody>{doc.items.map((it, i) => (
            <tr key={i} className="border-t border-[#E2E0D8]"><td className="py-2">{it.name}</td><td>{it.quantity}</td><td>$ {it.unit_price.toFixed(2)}</td><td>$ {it.subtotal.toFixed(2)}</td></tr>
          ))}</tbody>
        </table>
        <div className="text-right text-sm">
          <div>IVA: <strong>$ {Number(doc.iva_total).toFixed(2)}</strong></div>
          <div className="font-heading text-2xl font-black mt-2">Total: $ {Number(doc.total).toFixed(2)}</div>
        </div>
        {doc.sri_message && <div className="mt-5 p-4 bg-[#F3F2ED] border border-[#E2E0D8] rounded-lg text-sm"><p className="text-[11px] uppercase tracking-wider text-[#8A9A92] mb-1">Mensaje SRI</p><p>{doc.sri_message}</p></div>}
      </div>
    </div>
  );
}
